import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Calendar, Clock, X, RotateCcw, AlertCircle } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const STATUS_TONE = {
  confirmed: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  cancelled: "bg-zinc-800 text-mb-fg2",
  completed: "bg-zinc-800 text-mb-fg2",
  no_show: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function PatientAppointments() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedSlots, setReschedSlots] = useState([]);
  const [reschedDate, setReschedDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const refresh = (t = tab) => {
    setLoading(true);
    api.get(`/patient/appointments?status=${t}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(tab); }, [tab]);

  const doCancel = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/patient/appointments/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success("Appointment cancelled");
      setCancelTarget(null); setCancelReason("");
      refresh();
    } catch (e) {
      setErr(apiError(e, "Cancel failed"));
    } finally { setBusy(false); }
  };

  const openReschedule = async (a) => {
    setReschedTarget(a); setErr("");
    const today = new Date(); const d = new Date(a.slot_start);
    setReschedDate(d.toISOString().slice(0, 10));
    const from = today.toISOString().slice(0, 10);
    const to = new Date(today.getTime() + 14 * 86400000).toISOString().slice(0, 10);
    const r = await api.get(`/patient/doctors/${a.doctor_id}/slots`, { params: { from, to } });
    setReschedSlots(r.data.slots || []);
  };

  const doReschedule = async (slotIso) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/patient/appointments/${reschedTarget.id}/reschedule`, { new_slot_start: slotIso });
      toast.success("Appointment rescheduled");
      setReschedTarget(null);
      refresh();
    } catch (e) {
      setErr(apiError(e, "Reschedule failed"));
    } finally { setBusy(false); }
  };

  const payNow = async (a) => {
    setBusy(true);

    try {
      const { data } = await api.post(`/patient/appointments/${a.id}/pay`, {});

      if (data.provider === "razorpay") {
        const loaded = await loadRazorpayScript();

        if (!loaded) {
          throw new Error("Unable to load Razorpay checkout");
        }

        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency || "INR",
          order_id: data.order_id,
          name: "MediBooks",
          description: `Appointment with ${a.doctor?.full_name || "Doctor"}`,

          handler: async (response) => {
            try {
              await api.post(`/patient/appointments/${a.id}/verify-payment`, {
                provider: "razorpay",
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });

              toast.success("Payment successful — appointment confirmed");
              refresh();
            } catch (verifyError) {
              toast.error(apiError(verifyError, "Payment verification failed"));
            }
          },

          modal: {
            ondismiss: () => {
              toast.error("Payment was cancelled. You can try again.");
            },
          },

          theme: {
            color: "#14b8a6",
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on("payment.failed", (response) => {
          const message =
            response?.error?.description ||
            response?.error?.reason ||
            "Payment failed. Try again.";
          toast.error(message);
        });
        razorpay.open();
        return;
      }

      if (data.provider === "stripe") {
        navigate(`/patient/appointments/${a.id}/checkout`, { state: { intent: data } });
        return;
      }

      if (data.status === "succeeded") {
        toast.success("Payment successful — appointment confirmed");
        refresh();
        return;
      }

      throw new Error(`Unsupported payment provider: ${data.provider || "unknown"}`);
    } catch (e) {
      toast.error(apiError(e, e?.message || "Payment error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="patient-appointments">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ my appointments</div>
        <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight font-light">Appointments</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-mb-surface border border-mb-border p-1 rounded-lg">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="data-[state=active]:bg-teal-500/10 data-[state=active]:text-teal-300 rounded-md">Upcoming</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past" className="data-[state=active]:bg-teal-500/10 data-[state=active]:text-teal-300 rounded-md">Past</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled" className="data-[state=active]:bg-teal-500/10 data-[state=active]:text-teal-300 rounded-md">Cancelled</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6">
          <div className="rounded-xl border border-mb-border bg-mb-surface">
            {loading ? (
              <div className="px-6 py-10 text-sm text-mb-fg2 text-center">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-6 py-12 text-sm text-mb-fg2 text-center" data-testid="appts-empty">
                No {tab} appointments. <Link to="/patient/hospitals" className="text-teal-400 hover:text-teal-300">Book one</Link>.
              </div>
            ) : (
              <ul className="divide-y divide-mb-border">
                {items.map((a) => (
                  <li key={a.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`appt-row-${a.id}`}>
                    <div className="h-10 w-10 rounded-full bg-teal-500/10 text-teal-300 flex items-center justify-center text-sm font-medium shrink-0">
                      {(a.doctor?.full_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{a.doctor?.full_name} <span className="text-mb-fg2">·</span> {a.doctor?.specialty}</div>
                      <div className="text-xs text-mb-fg2 truncate">{a.hospital?.name}</div>
                      <div className="text-xs text-mb-fg2 mt-1 flex items-center gap-2">
                        <Calendar className="h-3 w-3" /> {new Date(a.slot_start).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm text-white">{formatCurrency(a.consultation_fee)}</div>
                      <span className={`mt-1 inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${STATUS_TONE[a.status] || "bg-zinc-800 text-mb-fg2"}`}>{a.status.replace("_", " ")}</span>
                    </div>
                    {tab === "upcoming" && (
                      <div className="flex gap-2 shrink-0">
                        {a.status === "pending_payment" && (
                          <Button onClick={() => payNow(a)} disabled={busy} data-testid={`pay-${a.id}`}
                            className="bg-teal-500 hover:bg-teal-400 text-zinc-950 h-9 rounded-lg font-medium">{busy ? "Please wait…" : "Pay now"}</Button>
                        )}
                        <Button variant="ghost" onClick={() => openReschedule(a)} data-testid={`reschedule-${a.id}`}
                          className="hover:bg-mb-surface2 h-9"><RotateCcw className="h-4 w-4 mr-1" /> Reschedule</Button>
                        <Button variant="ghost" onClick={() => setCancelTarget(a)} data-testid={`cancel-${a.id}`}
                          className="hover:bg-red-500/10 text-red-400 h-9"><X className="h-4 w-4 mr-1" /> Cancel</Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && (setCancelTarget(null), setErr(""))}>
        <DialogContent className="bg-mb-surface border-mb-border text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light text-2xl">Cancel appointment</DialogTitle>
            <DialogDescription className="text-mb-fg2 text-sm">
              Cancellations within 2 hours of the appointment are not allowed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Reason (optional)</Label>
            <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason"
              className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg" />
            {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2" data-testid="cancel-error"><AlertCircle className="h-4 w-4 mt-0.5" />{err}</div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelTarget(null)} className="hover:bg-mb-surface2">Keep it</Button>
            <Button onClick={doCancel} disabled={busy} data-testid="confirm-cancel"
              className="bg-red-500 hover:bg-red-400 text-white">
              {busy ? "Cancelling…" : "Yes, cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={!!reschedTarget} onOpenChange={(o) => !o && (setReschedTarget(null), setErr(""))}>
        <DialogContent className="bg-mb-surface border-mb-border text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-light text-2xl">Reschedule</DialogTitle>
            <DialogDescription className="text-mb-fg2 text-sm">
              Pick a new available slot in the next 14 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {reschedSlots.length === 0 && <div className="text-sm text-mb-fg2">No available slots.</div>}
            {(() => {
              const grouped = {};
              for (const s of reschedSlots) (grouped[s.start.slice(0, 10)] ||= []).push(s);
              return Object.entries(grouped).map(([d, list]) => (
                <div key={d} className="rounded-lg border border-mb-border bg-mb-surface2 p-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-mb-fg2 mb-2">
                    {new Date(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {list.map(s => (
                      <button key={s.start} onClick={() => doReschedule(s.start)} disabled={busy}
                        data-testid={`resched-slot-${s.start}`}
                        className="px-3 py-1.5 rounded-lg bg-mb-surface border border-mb-border text-xs text-zinc-200 hover:border-teal-400 hover:text-teal-300 inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        {new Date(s.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </button>
                    ))}
                  </div>
                </div>
              ));
            })()}
            {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="resched-error">{err}</div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReschedTarget(null)} className="hover:bg-mb-surface2">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
